import { Avatar, AvatarFallback, getAvatarColor } from '@heidi/design-system/avatar';
import { Button } from '@heidi/design-system/button';
import { Popover, PopoverContent, PopoverTrigger } from '@heidi/design-system/popover';
import { focusRing } from '@heidi/design-system/styles';
import { TypographyP2 } from '@heidi/design-system/typography';
import { cn, getInitials } from '@heidi/utils';
import { FileOutput, FileText, FolderOpen, ListTodo, Plus, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { linkedContextItemLabel, type LinkedContextItem } from '../../contracts';

import { WorkHomeContextStrip } from '../projects/components/WorkHomeContextStrip';

const messages = defineMessages({
  visitKind: {
    defaultMessage: 'visit',
    id: 'Z1Te384xrJ',
    description: 'Object type for a visit attached to the Work Home composer',
  },
  patientKind: {
    defaultMessage: 'patient',
    id: '2vzvCH7jvn',
    description: 'Object type for a patient attached to the Work Home composer',
  },
  artifactKind: {
    defaultMessage: 'artifact',
    id: 'oHqKMsjzwN',
    description: 'Object type for an artifact attached to the Work Home composer',
  },
  taskKind: {
    defaultMessage: 'task',
    id: 'zLcVEi7GtX',
    description: 'Object type for a task attached to the Work Home composer',
  },
  collectionKind: {
    defaultMessage: 'collection',
    id: 'VIPW1Wmzqf',
    description: 'Object type for a collection attached to the Work Home composer',
  },
  preview: {
    defaultMessage: 'Preview attached {kind}: {name}',
    id: 'JbvGIB+A8Y',
    description: 'Accessible label for previewing context attached to Work Home',
  },
  remove: {
    defaultMessage: 'Remove attached {kind}: {name}',
    id: '9Gr4SoU6uN',
    description: 'Accessible label for removing context attached to Work Home',
  },
  choosePatient: {
    defaultMessage: 'Choose another patient',
    id: 'yK1Llsu3Pu',
    description: 'Accessible label for changing the patient attached to Work Home',
  },
});

export interface WorkHomePatientPickerBind {
  selectedIds: ReadonlySet<string>;
  onToggle: (patient: { id: string; name: string }) => void;
}

interface WorkHomeAttachedContextProps {
  item: LinkedContextItem;
  onRemove: () => void;
  onOpen?: () => void;
  onSelectPatient?: (patient: Extract<LinkedContextItem, { type: 'patient' }>) => void;
  renderPatientPicker?: (bind: WorkHomePatientPickerBind) => ReactNode;
}

/** Object context docked above the compact Work Home composer. */
export function WorkHomeAttachedContext({
  item,
  onRemove,
  onOpen,
  onSelectPatient,
  renderPatientPicker,
}: WorkHomeAttachedContextProps): JSX.Element {
  const intl = useIntl();
  const [patientPickerOpen, setPatientPickerOpen] = useState(false);
  const name = linkedContextItemLabel(item);
  const kind = intl.formatMessage(
    item.type === 'session'
      ? messages.visitKind
      : item.type === 'patient'
        ? messages.patientKind
        : item.type === 'task'
          ? messages.taskKind
          : item.type === 'collection'
            ? messages.collectionKind
            : messages.artifactKind
  );
  const Icon =
    item.type === 'session'
      ? FileText
      : item.type === 'task'
        ? ListTodo
        : item.type === 'collection'
          ? FolderOpen
          : FileOutput;
  const selectedId = item.type === 'patient' ? (item.patientId ?? item.patientName) : '';
  const content = (
    <>
      {item.type === 'patient' ? (
        <Avatar size="xs" color={getAvatarColor(name)} aria-hidden="true">
          <AvatarFallback>{getInitials(name).charAt(0)}</AvatarFallback>
        </Avatar>
      ) : (
        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-surface">
          <Icon className="size-3.5 text-text-secondary" aria-hidden />
        </span>
      )}
      <TypographyP2 className="max-w-56 truncate font-normal text-text-primary">
        {name}
      </TypographyP2>
    </>
  );

  return (
    <WorkHomeContextStrip
      placement="above"
      testId="work-home-attached-context"
      className="justify-start"
    >
      <div className="flex min-w-0 items-center gap-2">
        <div className="flex h-9 min-w-0 items-center rounded-full border border-border bg-background-secondary p-1 shadow-sm">
          {onOpen ? (
            <Button
              type="button"
              variant="ghost"
              onClick={onOpen}
              className={cn(
                'h-auto min-w-0 justify-start gap-2 rounded-full px-1.5',
                focusRing
              )}
              aria-label={intl.formatMessage(messages.preview, { kind, name })}
            >
              {content}
            </Button>
          ) : (
            <div className="flex min-w-0 items-center gap-2 px-1.5">{content}</div>
          )}

          <Button
            type="button"
            variant="ghost"
            size="sm"
            iconOnly
            onClick={onRemove}
            aria-label={intl.formatMessage(messages.remove, { kind, name })}
            className="size-7 rounded-full text-text-secondary hover:bg-surface-hover"
          >
            <X size={14} aria-hidden />
          </Button>
        </div>

        {item.type === 'patient' && onSelectPatient && renderPatientPicker ? (
          <Popover open={patientPickerOpen} onOpenChange={setPatientPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                iconOnly
                aria-label={intl.formatMessage(messages.choosePatient)}
                className="size-9 rounded-full border border-border bg-background-secondary text-text-secondary shadow-sm hover:bg-surface-hover"
              >
                <Plus className="size-4" aria-hidden />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              side="bottom"
              sideOffset={8}
              className="w-80 p-3"
            >
              {renderPatientPicker({
                selectedIds: new Set([selectedId]),
                onToggle: (patient) => {
                  onSelectPatient({
                    type: 'patient',
                    patientName: patient.name,
                    ...(patient.id === patient.name ? {} : { patientId: patient.id }),
                  });
                  setPatientPickerOpen(false);
                },
              })}
            </PopoverContent>
          </Popover>
        ) : null}
      </div>
    </WorkHomeContextStrip>
  );
}
